import { MULTI_ANALYSIS_MEASURE_TYPE, type IMultiAnalysisActiveMeasureWindow } from "../types";

export enum MULTI_ANALYSIS_TEMPLATE_TYPE {
  STANDARD = 0,
  CUSTOM = 1,
}

export interface IMultiAnalysisTemplateCell {
  id: string;
  row: number;
  column: number;
  measureType: MULTI_ANALYSIS_MEASURE_TYPE;
  filters: Record<string, unknown>;
}

export interface IMultiAnalysisTemplateLayout {
  rows: number;
  columns: number;
}

export interface INormalizedAnalysisTemplateSettings {
  layout: IMultiAnalysisTemplateLayout;
  cells: IMultiAnalysisTemplateCell[];
}

export interface INormalizedAnalysisTemplate {
  idAnalysisTemplate: number;
  name: string;
  type: MULTI_ANALYSIS_TEMPLATE_TYPE;
  idUser: number | null;
  isPublic: boolean;
  settings: INormalizedAnalysisTemplateSettings;
}

const DEFAULT_LAYOUT: IMultiAnalysisTemplateLayout = {
  rows: 1,
  columns: 1,
};

const FALLBACK_MEASURE_TYPES = [
  MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM,
  MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM,
  MULTI_ANALYSIS_MEASURE_TYPE.ORBIT,
  MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND,
] as const;

const parseSettings = (settings: unknown): Record<string, unknown> => {
  if (typeof settings === "string") {
    try {
      const parsed = JSON.parse(settings) as unknown;

      return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }

  return typeof settings === "object" && settings !== null && !Array.isArray(settings)
    ? (settings as Record<string, unknown>)
    : {};
};

const toRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const normalizePositiveInteger = (value: unknown, fallback: number): number => {
  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 1) {
    return fallback;
  }

  return numericValue;
};

const normalizeMeasureType = (value: unknown, fallback: MULTI_ANALYSIS_MEASURE_TYPE): MULTI_ANALYSIS_MEASURE_TYPE => {
  if (Object.values(MULTI_ANALYSIS_MEASURE_TYPE).includes(value as MULTI_ANALYSIS_MEASURE_TYPE)) {
    return value as MULTI_ANALYSIS_MEASURE_TYPE;
  }

  return fallback;
};

const normalizeFilters = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const cellId = (row: number, column: number): string => `${row}:${column}`;

const fallbackMeasureTypeByIndex = (index: number): MULTI_ANALYSIS_MEASURE_TYPE =>
  FALLBACK_MEASURE_TYPES[index % FALLBACK_MEASURE_TYPES.length];

export const normalizeAnalysisTemplateSettings = (settings: unknown): INormalizedAnalysisTemplateSettings => {
  const parsedSettings = parseSettings(settings);
  const rawLayout = parseSettings(parsedSettings.layout);
  const layout = {
    rows: normalizePositiveInteger(rawLayout.rows, DEFAULT_LAYOUT.rows),
    columns: normalizePositiveInteger(rawLayout.columns, DEFAULT_LAYOUT.columns),
  };
  const rawCells = Array.isArray(parsedSettings.cells) ? parsedSettings.cells : [];
  const cellsByPosition = new Map<string, Record<string, unknown>>();

  rawCells.forEach(rawCell => {
    const cell = parseSettings(rawCell);
    const row = Number(cell.row);
    const column = Number(cell.column);

    if (
      Number.isInteger(row) &&
      Number.isInteger(column) &&
      row >= 0 &&
      column >= 0 &&
      row < layout.rows &&
      column < layout.columns
    ) {
      cellsByPosition.set(cellId(row, column), cell);
    }
  });

  const cells: IMultiAnalysisTemplateCell[] = [];

  for (let row = 0; row < layout.rows; row += 1) {
    for (let column = 0; column < layout.columns; column += 1) {
      const index = row * layout.columns + column;
      const rawCell = cellsByPosition.get(cellId(row, column));
      const fallbackMeasureType = fallbackMeasureTypeByIndex(index);

      cells.push({
        id: cellId(row, column),
        row,
        column,
        measureType: normalizeMeasureType(rawCell?.viewType, fallbackMeasureType),
        filters: normalizeFilters(rawCell?.filters),
      });
    }
  }

  return { layout, cells };
};

const toNumber = (value: unknown): number => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : 0;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const toBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    return value === "1" || value.toLowerCase() === "true";
  }

  return false;
};

const normalizeTemplateType = (value: unknown): MULTI_ANALYSIS_TEMPLATE_TYPE => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) && numericValue === 0
    ? MULTI_ANALYSIS_TEMPLATE_TYPE.STANDARD
    : MULTI_ANALYSIS_TEMPLATE_TYPE.CUSTOM;
};

export const normalizeAnalysisTemplate = (template: unknown): INormalizedAnalysisTemplate => {
  const rawTemplate = toRecord(template);

  return {
    idAnalysisTemplate: toNumber(rawTemplate.idAnalysisTemplate),
    name: String(rawTemplate.name ?? rawTemplate.Name ?? ""),
    type: normalizeTemplateType(rawTemplate.type ?? rawTemplate.Type),
    idUser: toNullableNumber(rawTemplate.idUser),
    isPublic: toBoolean(rawTemplate.isPublic),
    settings: normalizeAnalysisTemplateSettings(rawTemplate.settings ?? rawTemplate.Settings),
  };
};

export const selectDefaultAnalysisTemplate = (
  templates: readonly INormalizedAnalysisTemplate[],
): INormalizedAnalysisTemplate | null =>
  templates.find(template => template.type === MULTI_ANALYSIS_TEMPLATE_TYPE.STANDARD) ?? templates[0] ?? null;

export const isOwnAnalysisTemplate = (template: INormalizedAnalysisTemplate, userId: number): boolean =>
  template.idUser !== null && template.idUser === userId;

export const isSharedAnalysisTemplate = (template: INormalizedAnalysisTemplate, userId: number): boolean =>
  template.type === MULTI_ANALYSIS_TEMPLATE_TYPE.CUSTOM &&
  template.isPublic &&
  template.idUser !== null &&
  template.idUser !== userId;

export const isStandardAnalysisTemplate = (template: INormalizedAnalysisTemplate): boolean =>
  template.type === MULTI_ANALYSIS_TEMPLATE_TYPE.STANDARD;

export const canEditAnalysisTemplate = (
  template: INormalizedAnalysisTemplate,
  userId: number,
  isAdmin: boolean,
): boolean => isOwnAnalysisTemplate(template, userId) || (isAdmin && isStandardAnalysisTemplate(template));

export const canDeleteAnalysisTemplate = (
  template: INormalizedAnalysisTemplate,
  userId: number,
  isAdmin: boolean,
): boolean =>
  Boolean(template.idAnalysisTemplate) &&
  (isOwnAnalysisTemplate(template, userId) || (isAdmin && isStandardAnalysisTemplate(template)));

export interface IFilterAnalysisTemplatesOptions {
  userId: number;
  showStandardTemplates: boolean;
  showPublicTemplates: boolean;
}

export const filterAnalysisTemplatesForUser = (
  templates: readonly INormalizedAnalysisTemplate[],
  { userId, showStandardTemplates, showPublicTemplates }: IFilterAnalysisTemplatesOptions,
): INormalizedAnalysisTemplate[] =>
  templates.filter(template => {
    if (isOwnAnalysisTemplate(template, userId)) {
      return true;
    }

    if (isStandardAnalysisTemplate(template)) {
      return template.isPublic && showStandardTemplates;
    }

    if (isSharedAnalysisTemplate(template, userId)) {
      return showPublicTemplates;
    }

    return false;
  });

const serializeCellFilters = (filters: Record<string, unknown>): Record<string, unknown> => ({
  rangeType: filters.rangeType ?? null,
  units: filters.units ?? null,
  presentation: filters.presentation ?? null,
  param1: filters.param1 ?? null,
  param2: filters.param2 ?? null,
  param3: filters.param3 ?? null,
  ...(Array.isArray(filters.Name) && filters.Name.length > 0 ? { Name: filters.Name } : {}),
  ...(typeof filters.Name === "string" && filters.Name.trim() ? { Name: filters.Name } : {}),
});

export const serializeAnalysisTemplateSettings = (
  settings: INormalizedAnalysisTemplateSettings,
): Record<string, unknown> => ({
  version: 1,
  layout: {
    rows: settings.layout.rows,
    columns: settings.layout.columns,
  },
  cells: settings.cells.map(cell => ({
    row: cell.row,
    column: cell.column,
    viewType: cell.measureType,
    filters: serializeCellFilters(cell.filters),
  })),
});

export const createDraftAnalysisTemplate = (
  userId: number,
  layout: IMultiAnalysisTemplateLayout = { rows: 2, columns: 2 },
): INormalizedAnalysisTemplate => ({
  idAnalysisTemplate: 0,
  name: "",
  type: MULTI_ANALYSIS_TEMPLATE_TYPE.CUSTOM,
  idUser: userId,
  isPublic: false,
  settings: normalizeAnalysisTemplateSettings({
    layout,
    cells: [],
  }),
});

export const cloneAnalysisTemplate = (template: INormalizedAnalysisTemplate): INormalizedAnalysisTemplate => ({
  ...template,
  settings: {
    layout: { ...template.settings.layout },
    cells: template.settings.cells.map(cell => ({
      ...cell,
      filters: { ...cell.filters },
    })),
  },
});

export const resizeAnalysisTemplateSettings = (
  settings: INormalizedAnalysisTemplateSettings,
  layout: IMultiAnalysisTemplateLayout,
): INormalizedAnalysisTemplateSettings =>
  normalizeAnalysisTemplateSettings({
    layout,
    cells: settings.cells
      .filter(cell => cell.row < layout.rows && cell.column < layout.columns)
      .map(cell => ({
        row: cell.row,
        column: cell.column,
        viewType: cell.measureType,
        filters: cell.filters,
      })),
  });

export const updateAnalysisTemplateCell = (
  settings: INormalizedAnalysisTemplateSettings,
  cellId: string,
  patch: Partial<Pick<IMultiAnalysisTemplateCell, "measureType" | "filters">>,
): INormalizedAnalysisTemplateSettings => ({
  ...settings,
  cells: settings.cells.map(cell =>
    cell.id === cellId
      ? {
          ...cell,
          measureType: patch.measureType ?? cell.measureType,
          filters: patch.filters ? { ...patch.filters } : cell.filters,
        }
      : cell,
  ),
});

export const resolveAnalysisTemplateById = (
  templates: readonly INormalizedAnalysisTemplate[],
  templateId: number | null,
): INormalizedAnalysisTemplate | null => {
  if (templateId === null) {
    return null;
  }

  return templates.find(template => template.idAnalysisTemplate === templateId) ?? null;
};

export const resolveAppliedAnalysisTemplate = (
  templates: readonly INormalizedAnalysisTemplate[],
  appliedTemplateId: number | null,
): INormalizedAnalysisTemplate | null =>
  resolveAnalysisTemplateById(templates, appliedTemplateId) ?? selectDefaultAnalysisTemplate(templates);

export const resolveActiveMeasureWindow = (
  activeMeasureWindow: IMultiAnalysisActiveMeasureWindow | null,
  template: INormalizedAnalysisTemplate | null,
): IMultiAnalysisActiveMeasureWindow | null => {
  if (!template) {
    return null;
  }

  const activeCell =
    activeMeasureWindow?.templateId === template.idAnalysisTemplate
      ? template.settings.cells.find(cell => cell.id === activeMeasureWindow.cellId)
      : null;
  const cell = activeCell ?? template.settings.cells[0];

  if (!cell) {
    return null;
  }

  return {
    templateId: template.idAnalysisTemplate,
    cellId: cell.id,
    row: cell.row,
    column: cell.column,
    measureType: cell.measureType,
  };
};
